/**
 * PermissionGate — wraps a page/section and blocks access if user lacks the required permission.
 */
import { usePermissions } from "@/hooks/usePermissions";
import { ArrowLeft, Lock } from "lucide-react";
import { Link } from "wouter";
import type { PermissionKey } from "@shared/permissions";
import { PERMISSION_LABELS } from "@shared/permissions";

interface PermissionGateProps {
  permission: PermissionKey;
  children: React.ReactNode;
}

export function PermissionGate({ permission, children }: PermissionGateProps) {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fffdf5] flex items-center justify-center">
        <div className="text-[#112451]/40">Loading...</div>
      </div>
    );
  }

  if (!hasPermission(permission)) {
    return (
      <div className="min-h-screen bg-[#fffdf5] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-[#112451]/5 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#112451]/40" />
          </div>
          <h2 className="font-['DM_Serif_Display'] text-2xl text-[#112451] mb-2">Access Restricted</h2>
          <p className="text-[#112451]/60 mb-6">
            You don't have access to <strong>{PERMISSION_LABELS[permission]}</strong>. Contact your admin if you need access.
          </p>
          <Link href="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#3057bf] hover:underline">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
